/**
 * Indicator registry API: definitions built from catalogue measures,
 * a draft → review → approved workflow, previews and published values.
 */
import api from './api';

const BASE = '/indicators';

export type IndicatorStatus = 'draft' | 'in_review' | 'approved' | 'retired';
export type IndicatorUnit = 'count' | 'ratio' | 'percent' | 'index';
export type ThresholdStatus = 'good' | 'watch' | 'poor';

export interface Option { code: string; label: string }

export interface ProfileDistrict {
  id: string;
  name: string;
  region: string | null;
}

export type ProfileOwnership = 'government' | 'private' | 'all';

export interface CatalogueMeasure {
  code: string;
  label: string;
  description: string;
  source: string;
  unit: string;
  by_year: boolean;
  /** No value for single schools: population by district, reconstructed cohort. */
  area_only?: boolean;
  /** Already a rate: used on its own, with no denominator. */
  computed?: boolean;
  dimensions: { code: string; basis: string }[];
}

export interface Catalogue {
  geo_levels: Option[];
  dimensions: (Option & { values: Option[] })[];
  measures: CatalogueMeasure[];
  units: Option[];
  categories: Option[];
  can_approve: boolean;
  can_manage_datasets: boolean;
}

export interface MeasureRef {
  measure: string;
  filters: Record<string, string[]>;
  /** Take this side from the previous (-1) or next (+1) year. */
  year_offset?: number;
  /** A formula part's name in words, e.g. "Girls enrolled". */
  label?: string;
}

/** Formula parts are named A to H. */
export const PART_NAMES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'] as const;

export interface FormulaCheck {
  ok: boolean;
  error?: string;
  reads_as?: string;
  unused?: string[];
}

export interface Threshold {
  status: ThresholdStatus;
  min?: number | null;
  max?: number | null;
}

export interface IndicatorDefinition {
  code: string;
  name: string;
  short_name: string;
  category: string;
  definition: string;
  rationale: string;
  interpretation: string;
  limitations: string;
  sdg_code: string;
  unit: IndicatorUnit;
  multiplier: number;
  decimals: number;
  numerator: MeasureRef;
  denominator: MeasureRef | null;
  /** Formula indicators: named parts and a formula over them, instead of numerator/denominator. */
  terms: Record<string, MeasureRef>;
  formula: string;
  match_schools: boolean;
  breakdowns: string[];
  thresholds: Threshold[];
}

export interface Indicator extends IndicatorDefinition {
  id: number;
  version: number;
  supersedes: number | null;
  status: IndicatorStatus;
  review_note: string;
  created_by_name: string;
  approved_by_name: string;
  approved_at: string | null;
  updated_at: string;
}

export interface ResultRow {
  /** Present on published values, absent on previews. */
  year?: number;
  geo_level: string;
  area_code: string;
  area_name: string;
  breakdown: string;
  breakdown_value: string;
  breakdown_label?: string;
  numerator: number | string;
  denominator: number | string | null;
  value: number | string | null;
  schools: number;
  status: ThresholdStatus | '';
  /** Formula previews: each part's total for the area. */
  parts?: Record<string, number>;
}

export interface PreviewRequest {
  numerator: MeasureRef;
  denominator: MeasureRef | null;
  terms: Record<string, MeasureRef>;
  formula: string;
  unit: IndicatorUnit;
  multiplier: number;
  decimals: number;
  match_schools: boolean;
  thresholds: Threshold[];
  year: number;
  geo_level: string;
  breakdown: string;
}

export interface Run {
  id: number;
  year: number;
  status: 'running' | 'succeeded' | 'failed';
  rows: number;
  warnings: string[];
  error: string;
  started_at: string;
}

export interface ScorecardCell {
  value: number | null;
  status: ThresholdStatus | '';
  schools: number;
}

export interface Scorecard {
  year: number;
  geo_level: string;
  indicators: {
    code: string; id: number; name: string; short_name: string; unit: IndicatorUnit;
    decimals: number; category: string; thresholds: Threshold[]; published: boolean;
  }[];
  areas: { code: string; name: string }[];
  national: Record<string, ScorecardCell>;
  cells: Record<string, Record<string, ScorecardCell>>;
  previous: Record<string, Record<string, number | null>>;
}

export interface DatasetField {
  id?: number;
  code: string;
  label: string;
  unit: string;
  help_text: string;
  order?: number;
}

export interface Dataset {
  id: number;
  code: string;
  name: string;
  description: string;
  is_active: boolean;
  dataset_fields: DatasetField[];
  value_count: number;
  updated_at: string;
}

export interface UploadError { row: number | null; column: string; message: string }

export interface DatasetUpload {
  id: number;
  year: number;
  file_name: string;
  accepted: boolean;
  schools: number;
  values_saved: number;
  errors: UploadError[];
  uploaded_by_name: string;
  uploaded_at: string;
}

export interface DatasetValues {
  year: number;
  schools: number;
  rows: { school_code: string; school_name: string; district: string; values: Record<string, number> }[];
}

export interface SchoolOption { code: string; name: string; district: string; region: string }

/** Turn a DRF error response into one readable message. */
export function errorMessage(err: any): string {
  const data = err?.response?.data;
  if (!data) return err?.message || 'Something went wrong.';
  if (typeof data === 'string') return data;
  if (data.error) return data.error;
  if (data.detail) return data.detail;
  return Object.entries(data)
    .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(' ') : msgs}`)
    .join(' · ');
}

export const indicatorRegistryApi = {
  catalogue: () => api.get<Catalogue>(`${BASE}/catalogue/`).then(r => r.data),

  list: (params: { latest?: boolean; status?: string; search?: string } = {}) =>
    api.get<Indicator[]>(`${BASE}/registry/`, {
      params: { ...params, latest: params.latest ? 'true' : undefined },
    }).then(r => r.data),

  versions: (code: string) =>
    api.get<Indicator[]>(`${BASE}/registry/`, { params: { code } }).then(r => r.data),

  get: (id: number) => api.get<Indicator>(`${BASE}/registry/${id}/`).then(r => r.data),

  create: (data: IndicatorDefinition) =>
    api.post<Indicator>(`${BASE}/registry/`, data).then(r => r.data),

  update: (id: number, data: Partial<IndicatorDefinition>) =>
    api.patch<Indicator>(`${BASE}/registry/${id}/`, data).then(r => r.data),

  remove: (id: number) => api.delete(`${BASE}/registry/${id}/`),

  transition: (id: number, action: 'submit' | 'approve' | 'reject' | 'retire' | 'new-version', note?: string) =>
    api.post<Indicator>(`${BASE}/registry/${id}/${action}/`, note ? { note } : {}).then(r => r.data),

  checkFormula: (formula: string, labels: Record<string, string>) =>
    api.post<FormulaCheck>(`${BASE}/registry/check-formula/`, { formula, labels }).then(r => r.data),

  preview: (data: PreviewRequest) =>
    api.post<{ rows: ResultRow[]; warnings: string[] }>(`${BASE}/registry/preview/`, data, { timeout: 120000 })
      .then(r => r.data),

  compute: (id: number, year: number) =>
    api.post<Run>(`${BASE}/registry/${id}/compute/`, { year }, { timeout: 300000 }).then(r => r.data),

  runs: (id: number) => api.get<Run[]>(`${BASE}/registry/${id}/runs/`).then(r => r.data),

  results: (id: number, params: { year?: number; geo_level?: string; breakdown?: string }) =>
    api.get<ResultRow[]>(`${BASE}/registry/${id}/results/`, { params }).then(r => r.data),

  scorecard: (year: number, geo_level: string) =>
    api.get<Scorecard>(`${BASE}/scorecard/`, { params: { year, geo_level } }).then(r => r.data),

  schools: (search: string) =>
    api.get<SchoolOption[]>(`${BASE}/schools/`, { params: { search } }).then(r => r.data),
};

export const datasetApi = {
  list: () => api.get<Dataset[]>(`${BASE}/datasets/`).then(r => r.data),

  create: (data: Omit<Dataset, 'id' | 'value_count' | 'updated_at'>) =>
    api.post<Dataset>(`${BASE}/datasets/`, data).then(r => r.data),

  update: (id: number, data: Partial<Dataset>) =>
    api.patch<Dataset>(`${BASE}/datasets/${id}/`, data).then(r => r.data),

  /** Download the Excel template for a year (triggers a browser download). */
  downloadTemplate: async (dataset: Dataset, year: number) => {
    const res = await api.get(`${BASE}/datasets/${dataset.id}/template/`, {
      params: { year }, responseType: 'blob', timeout: 120000,
    });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${dataset.code}_${year}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  },

  /** Resolves with the upload record whether or not it was accepted. */
  upload: (id: number, year: number, file: File) => {
    const form = new FormData();
    form.append('year', String(year));
    form.append('file', file);
    return api.post<DatasetUpload>(`${BASE}/datasets/${id}/upload/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000,
    })
      .then(r => r.data)
      .catch(err => {
        if (err?.response?.status === 400 && err.response.data?.errors) return err.response.data as DatasetUpload;
        throw err;
      });
  },

  uploads: (id: number) => api.get<DatasetUpload[]>(`${BASE}/datasets/${id}/uploads/`).then(r => r.data),

  values: (id: number, year: number) =>
    api.get<DatasetValues>(`${BASE}/datasets/${id}/values/`, { params: { year } }).then(r => r.data),

  saveSchool: (id: number, year: number, school_code: string, values: Record<string, number | null>) =>
    api.post(`${BASE}/datasets/${id}/values/`, { year, school_code, values }).then(r => r.data),
};

/** District education profiles: school-by-school Excel workbooks built from SIS. */
export const districtProfileApi = {
  /** Districts a profile can be made for. */
  districts: () =>
    api.get<ProfileDistrict[]>(`${BASE}/district-profile/districts/`).then(r => r.data),

  /** Download a district education profile workbook (triggers a browser download). */
  download: async (district: ProfileDistrict, year: number, ownership: ProfileOwnership) => {
    const res = await api.get(`${BASE}/district-profile/`, {
      params: { district: district.id, year, ownership }, responseType: 'blob', timeout: 300000,
    });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    const suffix = ownership === 'government' ? '' : `_${ownership}`;
    a.download = `District_profile_${district.name.replace(/ /g, '_')}_${year}${suffix}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  },
};
